import * as THREE from 'three';
import { ATMO, OCEAN } from '../config/tuning';
import { OCEAN_FRAGMENT, OCEAN_VERTEX } from '../render/shaders/ocean.glsl';
import { createWaterNormalTexture } from './generators/Textures';
import type { Sky } from './Sky';
import type { Terrain } from './Terrain';

/**
 * The sea (SPEC §3.9.1).
 *
 * The mesh is a *radial* grid centred on the aircraft rather than a flat plane. A uniform plane
 * has to choose between resolving a 27 m wavelength underneath the aircraft and reaching the
 * horizon 26 km away; a radial grid whose ring spacing grows as a power of the ring index does
 * both, with rings a few centimetres apart under the nose and hundreds of metres apart at the
 * limit of vision. One mesh, one draw call, no seams.
 *
 * Wave phase is computed from world position, so the swell is anchored to the world and the grid
 * slides underneath it — otherwise the sea would appear to travel with the aircraft, which is
 * the single most obvious way to make water look wrong.
 */

const DEG_TO_RAD = Math.PI / 180;

export class Ocean {
  readonly object3d: THREE.Mesh;

  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.ShaderMaterial;
  private readonly normalTexture: THREE.DataTexture;

  constructor(terrain: Terrain, sky: Sky, heightTexture: THREE.Texture) {
    this.normalTexture = createWaterNormalTexture();
    this.geometry = createRadialGrid();

    const waves: THREE.Vector4[] = [];
    const waveExtra: THREE.Vector3[] = [];
    for (const wave of OCEAN.waves) {
      const frequency = (2 * Math.PI) / wave.wavelengthM;
      // Deep-water dispersion: long swells travel faster than short chop, which is what stops
      // the three trains from marching in lockstep.
      const phaseSpeed = Math.sqrt((ATMO.G * wave.wavelengthM) / (2 * Math.PI)) * frequency;
      const heading = wave.directionDeg * DEG_TO_RAD;
      waves.push(
        new THREE.Vector4(Math.sin(heading), -Math.cos(heading), wave.amplitudeM, frequency),
      );
      waveExtra.push(new THREE.Vector3(wave.steepness / (frequency * wave.amplitudeM * OCEAN.waves.length), phaseSpeed, 0));
    }

    const heightSpan = terrain.maxHeightM - terrain.minHeightM;

    this.material = new THREE.ShaderMaterial({
      vertexShader: OCEAN_VERTEX,
      fragmentShader: OCEAN_FRAGMENT,
      // Double-sided: the camera can pass below sea level against a cliff or in a crash, and a
      // sea that vanishes when you are under it is worse than one that costs a backface test.
      side: THREE.DoubleSide,
      uniforms: {
        uWaves: { value: waves },
        uWaveExtra: { value: waveExtra },
        uTime: { value: 0 },
        uCameraPosition: { value: new THREE.Vector3() },
        uDisplacementFadeM: { value: OCEAN.displacementFadeM },

        uHeightMap: { value: heightTexture },
        uNormalMap: { value: this.normalTexture },
        uWorldOrigin: { value: new THREE.Vector2(-terrain.worldSizeM / 2, -terrain.worldSizeM / 2) },
        uWorldSize: { value: terrain.worldSizeM },
        uHeightScale: { value: heightSpan },
        uHeightOffset: { value: terrain.minHeightM },

        uSunDirection: { value: sky.sunDirection },
        uSunColour: { value: new THREE.Color() },
        uZenithColour: { value: new THREE.Color() },
        uHorizonColour: { value: new THREE.Color() },

        uShallowColour: { value: new THREE.Color(...OCEAN.shallowColour) },
        uDeepColour: { value: new THREE.Color(...OCEAN.deepColour) },
        uDepthRampM: { value: OCEAN.depthRampM },
        uFoamWidthM: { value: OCEAN.foamWidthM },
        uFoamStrength: { value: OCEAN.foamStrength },
        uFresnelF0: { value: OCEAN.fresnelF0 },
        uSpecularPower: { value: OCEAN.specularPower },

        uHazeColour: { value: new THREE.Color() },
        uHazeDensity: { value: sky.hazeDensity },
      },
    });

    this.object3d = new THREE.Mesh(this.geometry, this.material);
    // The grid follows the camera, so its bounds are meaningless to the culler.
    this.object3d.frustumCulled = false;
    this.object3d.renderOrder = 1;
  }

  /**
   * Advance the sea. `simTimeS` rather than wall time: two runs of the same replay must show the
   * same wave under the aircraft at the same tick.
   */
  update(cameraPosition: THREE.Vector3, simTimeS: number, sky: Sky): void {
    const uniforms = this.material.uniforms;
    uniforms['uTime'].value = simTimeS;
    (uniforms['uCameraPosition'].value as THREE.Vector3).copy(cameraPosition);
    (uniforms['uSunColour'].value as THREE.Color).copy(sky.sunColour);
    (uniforms['uZenithColour'].value as THREE.Color).copy(sky.zenithColour);
    (uniforms['uHorizonColour'].value as THREE.Color).copy(sky.horizonColour);
    (uniforms['uHazeColour'].value as THREE.Color).copy(sky.hazeColour);
    uniforms['uHazeDensity'].value = sky.hazeDensity;

    // The mesh itself stays at the origin; the shader offsets by the camera. Keeping the object
    // at the origin avoids losing float precision 12 km from the centre of the theatre.
    this.object3d.position.set(0, 0, 0);
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
    this.normalTexture.dispose();
  }
}

/**
 * A disc of concentric rings, radius growing as (i/N)^exponent so vertices crowd toward the
 * centre. Positions are relative to the camera; the shader adds the camera's world position.
 */
function createRadialGrid(): THREE.BufferGeometry {
  const { radialSegments, rings, innerRadiusM, outerRadiusM, ringExponent } = OCEAN;

  const vertexCount = radialSegments * (rings + 1);
  const positions = new Float32Array(vertexCount * 3);

  for (let ring = 0; ring <= rings; ring += 1) {
    const t = ring / rings;
    const radius = innerRadiusM + (outerRadiusM - innerRadiusM) * t ** ringExponent;
    for (let segment = 0; segment < radialSegments; segment += 1) {
      const angle = (segment / radialSegments) * Math.PI * 2;
      const index = (ring * radialSegments + segment) * 3;
      positions[index] = Math.cos(angle) * radius;
      positions[index + 1] = 0;
      positions[index + 2] = Math.sin(angle) * radius;
    }
  }

  const indices = new Uint32Array(radialSegments * rings * 6);
  let cursor = 0;
  for (let ring = 0; ring < rings; ring += 1) {
    for (let segment = 0; segment < radialSegments; segment += 1) {
      const next = (segment + 1) % radialSegments;
      const a = ring * radialSegments + segment;
      const b = ring * radialSegments + next;
      const c = (ring + 1) * radialSegments + segment;
      const d = (ring + 1) * radialSegments + next;
      // Wound the opposite way round from a Cartesian grid on purpose. Mapping (angle, radius)
      // to (x, z) has a negative Jacobian, so the naive order makes every triangle back-facing
      // and the entire sea is culled — visible as an ocean that draws but never appears.
      indices[cursor] = a;
      indices[cursor + 1] = b;
      indices[cursor + 2] = c;
      indices[cursor + 3] = b;
      indices[cursor + 4] = d;
      indices[cursor + 5] = c;
      cursor += 6;
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  return geometry;
}
